import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import config from "../../config";

const sections = [
  {
    id: "what-is-aac",
    heading: "What is Augmentative and Alternative Communication (AAC)?",
    body: [
      `AAC stands for Augmentative and Alternative Communication. It encompasses all forms of communication — other than oral speech — that are used to express thoughts, needs, wants, and ideas.`,
      `We all use AAC every single day! When we wave hello, text a friend, point to a menu item, or use emojis, we are using alternative forms of communication. For individuals who are non-speaking, minimally speaking, or whose speech is unreliably understood, AAC provides a vital bridge to connection and self-expression.`,
      `AAC is categorized into two main forms: Unaided AAC (gestures, body language, facial expressions, sign language) and Aided AAC (picture communication boards, speech-generating devices, tablets with specialized communication apps like Proloquo2Go or TouchChat).`
    ]
  },
  {
    id: "myths",
    heading: "Debunking Common AAC Myths",
    body: [
      `Myth 1: AAC prevents or delays spoken speech.`,
      `Fact: Decades of scientific research prove the exact opposite! AAC supports and often stimulates natural speech development by reducing frustration and building language processing pathways.`,
      `Myth 2: A child must reach a certain age or skill level before introducing AAC.`,
      `Fact: There are NO prerequisites for AAC. Everyone has the right to communicate from the earliest age possible. Communication is taught through modeling and real-world immersion, not mastery of prerequisite tests.`,
      `Myth 3: AAC is only a last resort when speech therapy fails.`,
      `Fact: AAC is a powerful proactive tool introduced at any stage to empower individuals with robust communication options immediately.`
    ]
  },
  {
    id: "awareness",
    heading: "Why AAC Awareness Month Matters",
    body: [
      `October is celebrated globally as AAC Awareness Month. It is a dedicated time to raise awareness about augmentative and alternative communication, promote inclusion, and amplify the voices of AAC users worldwide.`,
      `Awareness helps break down societal barriers, educates educators and healthcare providers, and encourages public spaces to be accessible for individuals using high-tech and low-tech communication systems.`
    ]
  },
  {
    id: "commitment",
    heading: "Ray of Hope's Commitment to AAC Inclusion",
    body: [
      `At Ray of Hope Center for Autism, AAC is integrated into our Speech & Language Pathology and multidisciplinary programs. We evaluate individual communication strengths, configure customized high-tech and low-tech AAC solutions, and train families and caregivers to model AAC naturally in daily routines.`,
      `Everyone deserves a voice. This AAC Awareness Month and every month, we celebrate multimodal communication and champion every child's right to express themselves with dignity and joy.`
    ]
  }
];

export default function AACAwarenessBlogPost() {
  const animRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    animRefs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  const ref = (el) => {
    if (el && !animRefs.current.includes(el)) animRefs.current.push(el);
  };

  return (
    <div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');

        .aac-blog-wrap {
          background-color: #F8FAF7;
          color: #1F2937;
          font-family: 'Plus Jakarta Sans', sans-serif;
          line-height: 1.8;
          padding-bottom: 4rem;
        }

        .aac-blog-top-band {
          background: #0D9488;
          padding: 0.85rem clamp(1.5rem, 6vw, 8rem);
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .aac-blog-top-band .tag {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: #CCFBF1;
        }
        .aac-blog-top-band .dot { color: #5EEAD4; font-size: 0.5rem; }
        .aac-blog-top-band .org { font-size: 0.8rem; color: rgba(255,255,255,0.85); }

        .aac-hero {
          padding: 4rem clamp(1.5rem, 6vw, 8rem) 2rem;
          max-width: 1100px;
          margin: 0 auto;
        }
        .aac-hero-eyebrow {
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #0D9488;
          margin-bottom: 0.75rem;
        }
        .aac-hero h1 {
          font-family: 'Outfit', sans-serif;
          font-size: clamp(2rem, 4vw, 3.2rem);
          font-weight: 700;
          color: #111827;
          line-height: 1.25;
          margin-bottom: 1.25rem;
        }
        .aac-hero-byline {
          font-size: 0.95rem;
          color: #4B5563;
        }

        .aac-content-card {
          max-width: 900px;
          margin: 0 auto 3rem;
          background: #FFFFFF;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          padding: clamp(2rem, 5vw, 3.5rem);
        }

        .aac-section {
          margin-bottom: 2.5rem;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .aac-section.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .aac-section h2 {
          font-family: 'Outfit', sans-serif;
          font-size: 1.6rem;
          font-weight: 700;
          color: #0F766E;
          margin-bottom: 1rem;
          border-bottom: 2px solid #CCFBF1;
          padding-bottom: 0.5rem;
        }

        .aac-section p {
          font-size: 1.05rem;
          color: #374151;
          margin-bottom: 1rem;
        }

        .aac-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.75rem;
          background-color: #0F766E;
          color: #FFFFFF !important;
          border-radius: 50px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
        }
        .aac-back-btn:hover {
          background-color: #0D9488;
          transform: translateX(-3px);
        }
      `}</style>

      <div className="aac-blog-wrap">
        <section className="page-header">
          <div 
            className="page-header-bg" 
            style={{ backgroundImage: `url(${config.baseUrl}/assets/images/backgrounds/blogpage-Header-1894-434.jpg)` }}
          ></div>
          <div className="container">
            <div className="page-header__inner">
              <h2>AAC Awareness Month</h2>
              <ul className="thm-breadcrumb list-unstyled">
                <li><Link to="/">Home</Link></li>
                <li><span>/</span></li>
                <li><Link to="/blogs">Blogs</Link></li>
                <li><span>/</span></li>
                <li><span>AAC Awareness</span></li>
              </ul>
            </div>
          </div>
        </section>

        <div className="aac-blog-top-band">
          <span className="tag">Speech Therapy &amp; AAC</span>
          <span className="dot">●</span>
          <span className="org">Ray of Hope Center for Autism</span>
        </div>

        <header className="aac-hero">
          <p className="aac-hero-eyebrow">Awareness · Augmentative and Alternative Communication</p>
          <h1>AAC Awareness Month: Celebrating Multimodal Communication and Every Voice</h1>
          <p className="aac-hero-byline">
            Written by <strong>RHCA Speech Therapy Team</strong> · Speech-Language Pathology Department
          </p>
        </header>

        <main className="container">
          <div className="aac-content-card">
            {sections.map((section) => (
              <div key={section.id} id={section.id} className="aac-section" ref={ref}>
                <h2>{section.heading}</h2>
                {section.body.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            ))}

            <div className="text-center mt-5">
              <Link to="/blogs" className="speech-back-btn aac-back-btn">
                <i className="fas fa-arrow-left"></i> Back to All Blogs
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
